/**
 * @file   src\components\LocationInformation.tsx
 * @brief  Location information component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { React,useState, useEffect,  Button, useRef, useNavigate, Modal, useParams } from '../utils/thirdpartyComponents';
import { Stepper, Step } from 'react-form-stepper';
import LocationInfoForm from './LocationInfoForm';
import BankingInfoForm from './BankingInfoForm';
import EquipmentInfoForm from './EquipmentInfoForm';
import CWOSignupBankInformation from '../Pages/CWOSignupBankInformation';
import { pageURLs } from '../utils/pageUrls';
import { MessageToaster } from "../utils/toastUtil";
import axios from '../config/axiosConfig';
import { LocationApi, UsersApi } from "../utils/apiUrls";
import { LocationData, BankDatanew, EquipmentData } from '../interfaces/generalInterface';
import { fetchCompanyAddressDetails, isUnitedStates, resolveLocationCountryState } from '../utils/countryState';
import { getFromLocalStorage } from '../utils/helper';
import { AddLocation } from '../interfaces/addLocationInterface';
import FullLoader from '../components/FullLoader';
import { EquipmentType, userType } from '../utils/enums';
import { getLocationFleetRequestFields } from '../utils/locationFleetSettings';
import { getLocationFeatureRequestFields } from '../utils/locationFeatureSettings';
import { useStripe } from "@stripe/react-stripe-js";


const AddLocationInfo = ({ edit, cuserType }: AddLocation) => {

    const param = useParams();
    const current_carwash_id = param.id ? parseInt(param.id) : 0;

    const [loading, setLoading] = useState(false);
    const [add_new_equipment, setAdd_new_equipment] = useState(0);
    const [location_id_result, setLocation_id_result] = useState(0);
    // get user details from session

    // Instantiate MessageToaster
    const toaster = new MessageToaster();

    const [locationData, setLocationData] = useState<LocationData>();
    const [achBankData, setAchBankData] = useState<any>();
    const [bankingData, setBankingData] = useState<BankDatanew>();
    const [equipmentData, setEquipmentData] = useState<EquipmentData>();
    const stripe: any = useStripe();
    const storageData = getFromLocalStorage('CARWASH_OWNER_DATA');

    //State to handle active step
    const [activeStep, setActiveStep] = useState(0);
    const [showEquipmentSuccesspoup, setShowEquipmentSuccesspoup] = useState<boolean>(false)
    // To handle validation of each step
    const validateStep = useRef<() => Promise<boolean>>(() => Promise.resolve(true)); // Initialize with a no-op function
    const [refreshEquipmentForm, setRefreshEquipmentForm] = useState<boolean>(false)
    const [showLocationAddSuccesspoup, setLocationAddSuccesspoup] = useState<boolean>(false)

    const navigate = useNavigate()

    const [companyCountry, setCompanyCountry] = useState<string | null>(null);
    const showAchBankStep = cuserType === userType['CARWASH-OWNER'] && isUnitedStates(companyCountry);
    const paymentStepIndex = showAchBankStep ? 2 : 1;
    const bayStepIndex = showAchBankStep ? 3 : 2;

    //Steps included
    const steps = showAchBankStep
        ? ['Location Info', 'Bank Information', 'Payment Info', 'Bay Info']
        : ['Location Info', 'Payment Info', 'Bay Info'];

    useEffect(() => {
        const loadCompanyCountry = async () => {
            if (cuserType !== userType['CARWASH-OWNER']) {
                setCompanyCountry('');
                return;
            }
            try {
                const company = await fetchCompanyAddressDetails(true, current_carwash_id || undefined);
                setCompanyCountry(company?.countryCode || '');
            } catch (error: any) {
                toaster.toastError(error.message);
                setCompanyCountry('');
            }
        };
        loadCompanyCountry();
    }, [cuserType, current_carwash_id]);

    useEffect(() => {
        // Scroll to the top of the page when the activeStep changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeStep]);

    // On clicking Next step
    const handleNext = async () => {
        const isValid = await validateStep.current();

        if (isValid) {
            if (activeStep === steps.length - 1) {
                if (edit) {
                    if (cuserType !== userType['CARWASH-OWNER']) {
                        navigate(`/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}`);
                    } else {
                        navigate(`/${pageURLs.carwash_locations}`);
                    }
                } else {
                    // if (cuserType !== userType['CARWASH-OWNER']) {
                    await submitData(); // Call the API when Finish is clicked

                }
            } else {
                setActiveStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
            }
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
    };

    const handleDataChange = (step: number, data: any) => {
        if (step === 0) setLocationData(data);
        if (step === 1) setAchBankData(data);
        if (step === 2) setBankingData(data);
        if (step === 3) setEquipmentData(data);
    };

    const submitCwoAchBankInfo = async (locationId?: number) => {
        const resolvedLocationId = Number(
            locationId || storageData?.locationId || storageData?.locationID || storageData?.LocationId || storageData?.LocationID || 0
        );

        if (!resolvedLocationId) {
            toaster.toastError("Location details are not available. Please complete Location Info again.");
            return false;
        }

        if (!achBankData) {
            toaster.toastError("Please enter bank information.");
            return false;
        }

        if (!stripe) {
            toaster.toastError("Stripe is not initialized");
            return false;
        }

        try {
            setLoading(true);
            const responseclient_secret = await axios.get(`${UsersApi.CARWASH_OWNER_GET_CLIENT_SECRET}/${resolvedLocationId}`);
            if (responseclient_secret?.data?.error !== 0) {
                toaster.toastError(responseclient_secret?.data?.message || "Unable to set up ACH bank account.");
                return false;
            }

            const clientSecret = responseclient_secret.data.data.clientSecret;
            const setupIntentResult: any = await stripe.confirmUsBankAccountSetup(
                clientSecret,
                {
                    payment_method: {
                        us_bank_account: {
                            account_number: achBankData.accountnumber,
                            routing_number: achBankData.routingnumber,
                            account_holder_type: (achBankData.accountholdertype === 1) ? 'individual' : 'company',
                        },
                        billing_details: {
                            name: achBankData.accountholdername,
                            email: storageData?.email
                        },
                    },
                }
            );

            if (!setupIntentResult?.setupIntent?.id) {
                toaster.toastError(setupIntentResult?.error?.message || "Some error occured please try again !");
                return false;
            }

            if (setupIntentResult?.setupIntent?.status === "requires_payment_method") {
                toaster.toastError("Confirmation failed. Please try again with a different bank account.");
                return false;
            }

            if (setupIntentResult?.setupIntent?.status === "succeeded") {
                const responseProfilepaymentUpdate = await axios.post(`${UsersApi.CARWASH_OWNER_MODIFY_BANK}/${resolvedLocationId}`, {
                    paymentMethod: setupIntentResult?.setupIntent?.payment_method
                });
                if (responseProfilepaymentUpdate?.data?.error !== 0) {
                    toaster.toastError(responseProfilepaymentUpdate?.data?.message || "Unable to save bank account.");
                    return false;
                }
            } else if (setupIntentResult?.setupIntent?.next_action?.type !== "verify_with_microdeposits") {
                toaster.toastError(setupIntentResult?.error?.message || "Some error occured please try again !");
                return false;
            }

            localStorage.setItem('CARWASH_OWNER_PAYMENTDATA_ADDED', 'true');
            return true;
        } catch {
            toaster.toastError("Failed to add bank account.");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const equipmentTypeSupportsComboWash = (type?: number) =>
        type === EquipmentType['In Bay'] || type === EquipmentType['Tunnel'];

    const buildBayRequestBody = (locationId: number) => {
        const bayType = Number(equipmentData?.equipmenttype) || 0;
        const showCategory = Boolean(locationData?.isComboWash && equipmentTypeSupportsComboWash(bayType));
        return {
            locationId,
            bayNumber: equipmentData?.baynumber ?? 0,
            bayName: equipmentData?.bayName ?? '',
            payStationBrand: equipmentData?.paystation ?? '',
            connectivityType: equipmentData?.internetconnectivity ?? 0,
            equipmentType: bayType,
            deviceId: equipmentData?.deviceid ?? 0,
            relayNumber: equipmentData?.relayNumber ?? 0,
            isPulse: Boolean(equipmentData?.isPulse),
            isPax: equipmentData?.isPax ? 1 : 0,
            isLPR: equipmentData?.isLpr ? 1 : 0,
            isComboWash: showCategory,
            washCategory: showCategory
                ? equipmentData?.washCategoryId != null
                    ? Number(equipmentData.washCategoryId)
                    : null
                : null,
            isUpsell: Boolean(equipmentTypeSupportsComboWash(bayType) && equipmentData?.isUpsell),
            isBuyUpEnabled: Boolean(equipmentData?.isBuyUpEnabled),
            lprMode: equipmentData?.isLpr
                ? equipmentData?.lprUsageType != null
                    ? Number(equipmentData.lprUsageType)
                    : null
                : null,
            secPerPulse: equipmentData?.secPerPulse ?? 0,
            minPulsesToStart: equipmentData?.minPulsesToStart ?? 0,
            minRuntime: Number((Number(equipmentData?.secPerPulse || 0) * Number(equipmentData?.minPulsesToStart || 0) / 60).toFixed(2)),
        };
    };

    const submitData = async () => {
        if (add_new_equipment === 1) {
            try {
                setLoading(true); // Show loader
                const equipmentRequestBody = buildBayRequestBody(location_id_result);
                const responseLocationEquipmentUpdate = (cuserType !== userType['CARWASH-OWNER']) ? await axios.post(LocationApi.ADD_LOCATION_EQUIPMENT, equipmentRequestBody) : await axios.post(LocationApi.CARWASH_OWNER_ADD_LOCATION_EQUIPMENT, equipmentRequestBody);
                if (responseLocationEquipmentUpdate?.data?.error === 0) {
                    showSuccessConfirmation();
                }
                else {
                    toaster.toastError(responseLocationEquipmentUpdate?.data?.message);
                }
            } catch{
                toaster.toastError('Failed to add location.');
            } finally {
                setLoading(false); // Hide loader
            }

        }
        else if (location_id_result === 0) {
            const { countryCode, stateCode } = await resolveLocationCountryState(
                locationData,
                cuserType === userType['CARWASH-OWNER'],
                current_carwash_id
            );
            const locationRequestBody = {
                carWashId: current_carwash_id,
                locationName: locationData?.locationName,
                email: locationData?.email,
                offset: locationData?.timezoneOffset !== undefined ? Number(locationData.timezoneOffset) : undefined,
                isComboWash: Boolean(locationData?.isComboWash),
                ...getLocationFleetRequestFields(locationData),
                ...getLocationFeatureRequestFields(locationData),
                hasDefaultAddress: locationData?.hasDefaultAddress,
                address1: locationData?.address1,
                address2: locationData?.address2,
                city: locationData?.city,
                countryCode,
                stateCode,
                zip: locationData?.zipcode,
            };
            try {
                setLoading(true); // Show loader
                const responseLocationUpdate = (cuserType !== userType['CARWASH-OWNER']) ? await axios.post(LocationApi.ADD_LOCATION, locationRequestBody) : await axios.post(LocationApi.CARWASH_OWNER_ADD_LOCATION, locationRequestBody);
                if (responseLocationUpdate?.data?.error === 0) {
                    if (responseLocationUpdate.data.data.locationId !== '' || responseLocationUpdate.data.data.locationId !== undefined) {
                        const createdLocationId = responseLocationUpdate.data.data.locationId;
                        setLocation_id_result(createdLocationId);
                            if (showAchBankStep && !edit) {
                                const achBankInfoSubmitted = await submitCwoAchBankInfo(createdLocationId);
                                if (!achBankInfoSubmitted) return;
                            }
                            const onwerlocbankingData = bankingData?.account === false ? {
                                locationId: createdLocationId,
                                PaymentMethod : bankingData?.PaymentMethod,
                                HasDefaultAccount: 0,
                                paymentKeys:{
                                    stripeSecretKey: bankingData?.stripeSecretKey.trim(),
                                    stripePublicKey: bankingData?.stripePublicKey.trim()
                                }
                            } :
                            {
                                locationId: createdLocationId,
                                HasDefaultAccount: 1,
                            } ;
                            const adminlocbankingData = bankingData?.account === false ? {
                                locationId: createdLocationId,
                                carwashid: current_carwash_id,
                                PaymentMethod : bankingData?.PaymentMethod,
                                HasDefaultAccount: 0,
                                paymentKeys:{
                                    stripeSecretKey: bankingData?.stripeSecretKey.trim(),
                                    stripePublicKey: bankingData?.stripePublicKey.trim()
                                }
                            } :
                            {
                                locationId: createdLocationId,
                                carwashid: current_carwash_id,
                                HasDefaultAccount: 1,
                            } ;
                            const responseLocationBankUpdate = (cuserType !== userType['CARWASH-OWNER']) ? await axios.post(LocationApi.UPDATE_LOCATION_PAYMENT_METHOD, adminlocbankingData) : await axios.post(LocationApi.CARWASH_OWNER_UPDATE_LOCATION_PAYMENT_METHOD, onwerlocbankingData);
                            if (responseLocationBankUpdate?.data?.error === 0) {
                                const equipmentRequestBody = buildBayRequestBody(createdLocationId);
                                const responseLocationEquipmentUpdate = (cuserType !== userType['CARWASH-OWNER']) ? await axios.post(LocationApi.ADD_LOCATION_EQUIPMENT, equipmentRequestBody) : await axios.post(LocationApi.CARWASH_OWNER_ADD_LOCATION_EQUIPMENT, equipmentRequestBody);
                                if (responseLocationEquipmentUpdate?.data?.error === 0) {
                                    showSuccessConfirmation();
                                }
                                else {
                                    toaster.toastError(responseLocationEquipmentUpdate?.data?.message);
                                }

                            }
                            else {
                                toaster.toastError(responseLocationBankUpdate?.data?.message);
                            }
                    }
                }
                else {
                    toaster.toastError(responseLocationUpdate?.data?.message);
                }
            } catch  {
                toaster.toastError('Failed to add location.');
            } finally {
                setLoading(false); // Hide loader
            }

        }
        else {
            try {
                setLoading(true); // Show loader
                    if (showAchBankStep && !edit) {
                        const achBankInfoSubmitted = await submitCwoAchBankInfo(location_id_result);
                        if (!achBankInfoSubmitted) return;
                    }
                    const onwerlocbankingData = bankingData?.account === false ? {
                        locationId: location_id_result,
                        PaymentMethod : bankingData?.paymentType,
                        HasDefaultAccount: 0,
                        paymentKeys:{
                            stripeSecretKey: bankingData?.stripeSecretKey.trim(),
                            stripePublicKey: bankingData?.stripePublicKey.trim()
                        }
                    } :
                    {
                        locationId: location_id_result,
                        HasDefaultAccount: 1,
                    } ;
                    const adminlocbankingData = bankingData?.account === false ? {
                        locationId: location_id_result,
                        carwashid: current_carwash_id,
                        PaymentMethod : bankingData?.paymentType,
                        HasDefaultAccount: 0,
                        paymentKeys:{
                            stripeSecretKey: bankingData?.stripeSecretKey.trim(),
                            stripePublicKey: bankingData?.stripePublicKey.trim()
                        }
                    } :
                    {
                        locationId: location_id_result,
                        carwashid: current_carwash_id,
                        HasDefaultAccount: 1,
                    } ;
                    const responseLocationBankUpdate = (cuserType !== userType['CARWASH-OWNER']) ? await axios.post(LocationApi.UPDATE_LOCATION_PAYMENT_METHOD, adminlocbankingData) : await axios.post(LocationApi.CARWASH_OWNER_UPDATE_LOCATION_PAYMENT_METHOD, onwerlocbankingData);
                    if (responseLocationBankUpdate?.data?.error === 0) {
                        const equipmentRequestBody = buildBayRequestBody(location_id_result);
                        const responseLocationEquipmentUpdate = (cuserType !== userType['CARWASH-OWNER']) ? await axios.post(LocationApi.ADD_LOCATION_EQUIPMENT, equipmentRequestBody) : await axios.post(LocationApi.CARWASH_OWNER_ADD_LOCATION_EQUIPMENT, equipmentRequestBody);
                        if (responseLocationEquipmentUpdate?.data?.error === 0) {
                            showSuccessConfirmation();
                        }
                        else {
                            toaster.toastError(responseLocationEquipmentUpdate?.data?.message);
                        }

                    }
                    else {
                        toaster.toastError(responseLocationBankUpdate?.data?.message);
                    }
            } catch {
                toaster.toastError('Failed to add location.');
            } finally {
                setLoading(false); // Hide loader
            }
        }
    };

    const showSuccessConfirmation = () => {
        setShowEquipmentSuccesspoup(true)
    }
    //Hide ppopup
    const hideEquipmentAddModal = () => {
        setRefreshEquipmentForm(true)
        setShowEquipmentSuccesspoup(false);
        setAdd_new_equipment(1);
        setEquipmentData({
            baynumber: 0,
            paystation: '0',
            internetconnectivity: 0,
            equipmenttype: 0,
            deviceid: 0,
            bayName:'',
            relayNumber:0,
            isPulse:false,
            isPax: false,
            isLpr: false,
            isComboWash: false,
            washCategoryId: null,
            isBuyUpEnabled: false,
            isUpsell: false,
            lprUsageType: null,
            secPerPulse:0,
            minPulsesToStart:0,
            minRuntime:0
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });

        //  window.location.reload()
    }
    const NoMoreEquipmentAction = () => {
        //navigate(`/superadmin/${pageURLs.carwash_locations}/1`);
        setShowEquipmentSuccesspoup(false);
        setRefreshEquipmentForm(true)
        if (cuserType !== userType['CARWASH-OWNER']) {
            navigate(`/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}`);
        } else {
            navigate(`/${pageURLs.carwash_locations}`);
        }

        // setLocationAddSuccesspoup(true)
    }

    const hideLocationAddModal = () => {
        window.location.reload()
    }
    const NoMoreLocationAction = () => {
        if (cuserType !== userType['CARWASH-OWNER']) {
            navigate(`/superadmin/${pageURLs.carwash_locations}/${current_carwash_id}`);
        } else {
            navigate(`/${pageURLs.carwash_locations}`);
        }
    }

    return (
        <div className='step-content-outer'>
            <div className='common-wrap location-info-wrapper'>
                <div className="title-block mx-auto text-center">
                    <h3 className='f-bold'>Locations</h3>
                    <p className='f-medium'>Add your service locations and reach out to all your prospective customers out there.</p>
                </div>

                <Stepper activeStep={activeStep} className='stepper-main' stepClassName='single-step'>
                    {steps.map((label,index) => (
                        <Step label={label} key={index}/>
                    ))}
                </Stepper>


                <div>
                    {activeStep === 0 && <LocationInfoForm edit={edit} onValidate={(validateFunc) => validateStep.current = validateFunc} onDataChange={(data) => handleDataChange(0, data)} m_formData={locationData} cuserType={cuserType} />}
                    {showAchBankStep && activeStep === 1 && (
                        <CWOSignupBankInformation
                            embedded={true}
                            onValidate={(validateFunc) => validateStep.current = validateFunc}
                            onDataChange={(data) => handleDataChange(1, data)}
                            m_formData={achBankData}
                        />
                    )}
                    {activeStep === paymentStepIndex && (
                        <BankingInfoForm
                            edit={edit}
                            onValidate={(validateFunc) => validateStep.current = validateFunc}
                            onDataChange={(data) => handleDataChange(2, data)}
                            m_formData_bank={bankingData}
                            cuserType={cuserType}
                        />
                    )}
                    {activeStep === bayStepIndex && (
                        <EquipmentInfoForm
                            edit={edit}
                            onValidate={(validateFunc) => validateStep.current = validateFunc}
                            onDataChange={(data) => handleDataChange(3, data)}
                            m_formData_equipment={equipmentData}
                            refreshEquipmentForm={refreshEquipmentForm}
                            cuserType={cuserType}
                            carwashid={current_carwash_id}
                            locationAllowsComboWash={Boolean(locationData?.isComboWash)}
                        />
                    )}

                    <div className='btn-action-sec d-flex align-items-center justify-content-end'>
                        <Button disabled={activeStep === 0} title="Back" variant="primary" className="secondary-btn" onClick={handleBack}>Back</Button>
                        <Button title={activeStep === steps.length - 1 ? 'Finish' : 'Next'} variant="primary" className="primary-btn" onClick={handleNext}>{activeStep === steps.length - 1 ? 'Finish' : 'Next'}</Button>
                    </div>
                </div>

            </div>
            <Modal
                show={showEquipmentSuccesspoup}
                onHide={hideEquipmentAddModal}
                centered
                className='add-modal'
            >
                <Modal.Body className='text-center'>
                    <h5 className='f-bold text-center'>Bay added successfully! Do you want to add another?</h5>
                    <div className="btn-sec d-grid">
                        <Button title="Not Now" variant="light" className="secondary-btn" onClick={NoMoreEquipmentAction}>Not Now</Button>
                        <Button title="Yes" variant="primary" className='primary-btn btn-main m-0' onClick={hideEquipmentAddModal}>Yes</Button>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal
                show={showLocationAddSuccesspoup}
                onHide={hideLocationAddModal}
                centered
                className='add-modal'
            >
                <Modal.Body className='text-center'>
                    <h5 className='f-bold text-center'> Location added successfully! Do you want add a new Location?</h5>
                    <div className="btn-sec d-grid">
                        <Button title="Not Now" variant="light" className="secondary-btn" onClick={NoMoreLocationAction}>Not Now</Button>
                        <Button title="Yes" variant="primary" className='primary-btn btn-main m-0' onClick={hideLocationAddModal}>Yes</Button>
                    </div>
                </Modal.Body>
            </Modal>
            {(loading || (cuserType === userType['CARWASH-OWNER'] && companyCountry === null)) && <FullLoader />}
        </div>
    );
};
export default AddLocationInfo;
